import { z } from 'zod';
import { PlanCode } from './plan';

export const EntitlementModelTier = z.enum(['lite', 'standard', 'premium', 'all']);
export type EntitlementModelTier = z.infer<typeof EntitlementModelTier>;

export const EntitlementFeatureValue = z.union([z.boolean(), z.number(), z.string()]);
export type EntitlementFeatureValue = z.infer<typeof EntitlementFeatureValue>;

export const EntitlementQuotaMap = z.record(z.string(), z.number().int().min(-1));
export type EntitlementQuotaMap = z.infer<typeof EntitlementQuotaMap>;

export const EntitlementFeatureMap = z.record(z.string(), EntitlementFeatureValue);
export type EntitlementFeatureMap = z.infer<typeof EntitlementFeatureMap>;

export const EntitlementValues = z
  .object({
    quotas: EntitlementQuotaMap,
    features: EntitlementFeatureMap,
    model_tier: EntitlementModelTier,
    storage_bytes: z.number().int().nonnegative(),
  })
  .strict();
export type EntitlementValues = z.infer<typeof EntitlementValues>;

export const EntitlementChangeKind = z.enum(['increase', 'decrease', 'unchanged']);
export type EntitlementChangeKind = z.infer<typeof EntitlementChangeKind>;

export const EntitlementFieldChange = z
  .object({
    path: z.string().min(1),
    kind: EntitlementChangeKind,
    before: z.union([z.boolean(), z.number(), z.string(), z.null()]),
    after: z.union([z.boolean(), z.number(), z.string(), z.null()]),
    effective: z.enum(['immediate', 'next_cycle']),
  })
  .strict();
export type EntitlementFieldChange = z.infer<typeof EntitlementFieldChange>;

export const PendingEntitlementChanges = z
  .object({
    version_id: z.string().min(1),
    effective_at: z.string().datetime(),
    fields: z.array(EntitlementFieldChange),
  })
  .strict();
export type PendingEntitlementChanges = z.infer<typeof PendingEntitlementChanges>;

export const ResolvedTenantEntitlement = z
  .object({
    tenant_id: z.string().min(1),
    plan_code: PlanCode,
    source_version_id: z.string().min(1),
    values: EntitlementValues,
    pending: PendingEntitlementChanges.nullable(),
    effective_at: z.string().datetime(),
  })
  .strict();
export type ResolvedTenantEntitlement = z.infer<typeof ResolvedTenantEntitlement>;

export const AdminPlanEntitlementDraft = EntitlementValues.extend({
  storage_mb: z.number().int().nonnegative(),
})
  .omit({ storage_bytes: true })
  .strict();
export type AdminPlanEntitlementDraft = z.infer<typeof AdminPlanEntitlementDraft>;

export const AdminPlanUpdateFields = z
  .object({
    display_name: z.string().trim().min(1).max(64),
    price_monthly_cents: z.number().int().min(0).max(100_000_000),
    price_yearly_cents: z.number().int().min(0).max(100_000_000),
    currency: z.literal('USD'),
    quotas: EntitlementQuotaMap,
    features: EntitlementFeatureMap,
    model_tier: EntitlementModelTier,
    storage_mb: z.number().int().min(0).max(1_000_000_000),
    is_active: z.boolean(),
    sort_order: z.number().int().min(0).max(1000),
    reason: z.string().trim().min(1).max(500),
  })
  .strict();
export type AdminPlanUpdateFields = z.infer<typeof AdminPlanUpdateFields>;

export const AdminPlanUpdatePreviewRequest = AdminPlanUpdateFields;
export type AdminPlanUpdatePreviewRequest = z.infer<typeof AdminPlanUpdatePreviewRequest>;

export const AdminPlanUpdateCommitRequest = AdminPlanUpdateFields.extend({
  state_token: z.string().min(16).max(512),
  idempotency_key: z.string().uuid(),
}).strict();
export type AdminPlanUpdateCommitRequest = z.infer<typeof AdminPlanUpdateCommitRequest>;

export const AdminPlanUpdatePreviewResponse = z
  .object({
    plan_code: PlanCode,
    state_token: z.string().min(1),
    changes: z.array(EntitlementFieldChange),
    affected_tenants: z.object({
      total: z.number().int().nonnegative(),
      immediate: z.number().int().nonnegative(),
      deferred: z.number().int().nonnegative(),
    }),
  })
  .strict();
export type AdminPlanUpdatePreviewResponse = z.infer<typeof AdminPlanUpdatePreviewResponse>;
